import type React from "react";
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { HeatmapColorScheme } from "../../types/gazeHeatmapTypes";
import type { CoverageStatistics } from "../../types/coverageStatistics";
import { generateLegendImage } from "../../utils/heatmapGeneration";

interface HeatmapLegendProps {
  colorScheme: HeatmapColorScheme;
  pointCount?: number;
  duration?: number;
  className?: string;
  /** Optional coverage statistics to display inline */
  coverageStats?: CoverageStatistics | null;
  /** Whether to show compact coverage stats inline */
  showCoverageInline?: boolean;
}

/**
 * Legend component showing the heatmap color scale and statistics
 */
const HeatmapLegend: React.FC<HeatmapLegendProps> = ({
  colorScheme,
  pointCount,
  duration,
  className = "",
  coverageStats,
  showCoverageInline = false,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const legendImage = generateLegendImage(colorScheme, 200, 16);
    canvas.width = legendImage.width;
    canvas.height = legendImage.height;
    ctx.putImageData(legendImage, 0, 0);
  }, [colorScheme]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m${secs.toString().padStart(2, "0")}s`;
  };

  return (
    <div className={`flex items-center gap-4 text-sm ${className}`}>
      {/* Color gradient legend */}
      <div className="flex items-center gap-2">
        <span className="text-gray-500">{t("statsNew.gazeHeatmap.lowDensity")}</span>
        <canvas
          ref={canvasRef}
          className="h-4 rounded"
          style={{ width: "120px", height: "12px" }}
        />
        <span className="text-gray-500">{t("statsNew.gazeHeatmap.highDensity")}</span>
      </div>

      {/* Separator */}
      <div className="h-4 w-px bg-gray-300" />

      {/* Stats */}
      <div className="flex items-center gap-3">
        {pointCount !== undefined && (
          <span className="text-gray-600">
            <span className="font-medium">{pointCount.toLocaleString()}</span>{" "}
            {t("statsNew.gazeHeatmap.points")}
          </span>
        )}
        {duration !== undefined && duration > 0 && (
          <span className="text-gray-600">
            {t("statsNew.gazeHeatmap.duration")}:{" "}
            <span className="font-medium">{formatDuration(duration)}</span>
          </span>
        )}

        {/* Inline coverage stats */}
        {showCoverageInline && coverageStats && (
          <>
            <div className="h-4 w-px bg-gray-300" />
            <span className="text-gray-600">
              {t("statsNew.coverage.coverage")}:{" "}
              <span className="font-medium">{coverageStats.coveragePercentage.toFixed(1)}%</span>
            </span>
            <span className="text-gray-600">
              {t("statsNew.coverage.entropy")}:{" "}
              <span className="font-medium">{coverageStats.entropy.toFixed(2)}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default HeatmapLegend;
